import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  BarChart3, 
  Settings,
  Menu,
  X,
  Briefcase,
  ListTree,
  Car
} from 'lucide-react';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminDriverApplications from '../components/admin/AdminDriverApplications';
import AdminDriverApplicationDetail from '../components/admin/AdminDriverApplicationDetail';
import AdminServiceProviderApplications from '../components/admin/AdminServiceProviderApplications';
import AdminServiceProviderApplicationDetail from '../components/admin/AdminServiceProviderApplicationDetail';
import UserManagement from '../components/admin/UserManagement';
import AdminServiceCategories from '../components/admin/AdminServiceCategories';
import AdminSystemSettings from '../components/admin/AdminSystemSettings';
import AdminTrips from '../components/admin/AdminTrips';
import AdminCareers from '../components/admin/AdminCareers';
import {useAuth} from '../hooks/useAuth';

type AdminView = 'dashboard' | 'driver-applications' | 'service-provider-applications' | 'service-categories' | 'careers' | 'users' | 'trips' | 'analytics' | 'settings' | 'system-settings';

const AdminPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<AdminView>('dashboard');
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [selectedServiceProviderApplicationId, setSelectedServiceProviderApplicationId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  const navigation = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'driver-applications', name: 'Driver Applications', icon: FileText },
    { id: 'service-provider-applications', name: 'Service Provider Applications', icon: Briefcase },
    { id: 'service-categories', name: 'Service Categories', icon: ListTree },
    { id: 'careers', name: 'Careers', icon: Briefcase },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'trips', name: 'Trips', icon: Car },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'system-settings', name: 'System Settings', icon: Settings },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  const handleViewApplication = (id: number) => {
    setSelectedApplicationId(id);
  };

  const handleViewServiceProviderApplication = (id: number) => {
    setSelectedServiceProviderApplicationId(id);
  };

  const handleBackToApplications = () => {
    setSelectedApplicationId(null);
  };

  const handleBackToServiceProviderApplications = () => {
    setSelectedServiceProviderApplicationId(null);
  };

  const handleApplicationUpdated = () => {
    // Refresh the applications list if needed
    // This could trigger a refetch in the AdminDriverApplications component
  };

  const handleServiceProviderApplicationUpdated = () => {
    // Refresh the service provider applications list if needed
  };

  const renderContent = () => {
    if (selectedApplicationId) {
      return (
        <AdminDriverApplicationDetail
          applicationId={selectedApplicationId}
          onBack={handleBackToApplications}
          onApplicationUpdated={handleApplicationUpdated}
        />
      );
    }

    if (selectedServiceProviderApplicationId) {
      return (
        <AdminServiceProviderApplicationDetail
          applicationId={selectedServiceProviderApplicationId}
          onBack={handleBackToServiceProviderApplications}
          onApplicationUpdated={handleServiceProviderApplicationUpdated}
        />
      );
    }

    switch (currentView) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'driver-applications':
        return <AdminDriverApplications onViewApplication={handleViewApplication} />;
      case 'service-provider-applications':
        return <AdminServiceProviderApplications onViewApplication={handleViewServiceProviderApplication} />;
      case 'users':
        return <UserManagement />;
      case 'trips':
        return <AdminTrips />;
      case 'service-categories':
        return <AdminServiceCategories />;
      case 'careers':
        return <AdminCareers />;
      case 'analytics':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600">Analytics dashboard coming soon...</p>
            </div>
          </div>
        );
      case 'settings':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Settings</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600">Platform settings coming soon...</p>
            </div>
          </div>
        );
      case 'system-settings':
        return <AdminSystemSettings />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <nav className="mt-6 px-6">
          <ul className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id && !selectedApplicationId && !selectedServiceProviderApplicationId;
              
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      setCurrentView(item.id as AdminView);
                      setSelectedApplicationId(null);
                      setSelectedServiceProviderApplicationId(null);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400'}`} />
                    {item.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 w-full p-6 border-t border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">{user?.first_name?.[0] || 'A'}</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{user?.first_name || 'Admin User'}</p>
              <p className="text-xs text-gray-500">{user?.email || 'admin@example.com'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {selectedApplicationId 
                ? 'Application Detail' 
                : selectedServiceProviderApplicationId 
                ? 'Service Provider Application Detail'
                : navigation.find(item => item.id === currentView)?.name || 'Dashboard'
              }
            </h1>
            <div className="w-6"></div> {/* Spacer */}
          </div>
        </div>

        {/* Content */}
        <main className="flex-1">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
